import React, { useState, useEffect } from "react"
import { Autocomplete, TextField, Button, Grid, CircularProgress, Snackbar, IconButton, Alert } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from "moment";
import axios from "axios";
import { APIURL } from "../../../config";

export default function WorkflowForm(props) {
    const [loading, setLoading] = useState(true);
    const [savingWfInstance, setSavingWfInstance] = useState(false);
    const [companyWorkflows, setCompanyWorkflows] = useState([]);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [customerList, setCustomerList] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [employeeList, setEmployeeList] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [wfStartDate, setWfStartDate] = useState(moment());
    const [wfEndDate, setWfEndDate] = useState(moment().add(1, 'w'));
    const [alertMessage, setAlertMessage] = useState('');
    const [alertType, setAlertType] = useState('');

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await fetchWorkflowData();
            await fetchCustomerData();
            await fetchEmployeeData();
            setLoading(false);
        };
        init();
    }, [props.comp_id, props.selectedProgramId]);

    useEffect(() => {
        if (selectedCustomer && !customerList.some((customer) => customer.cus_id === selectedCustomer.cus_id)) {
            setSelectedCustomer(null);
        }
        if (selectedEmployee && !employeeList.some((employee) => employee.emp_id === selectedEmployee.emp_id)) {
            setSelectedEmployee(null);
        }
    }, [customerList, employeeList, selectedCustomer, selectedEmployee]);

    const fetchWorkflowData = async () => {
        await axios.get(APIURL + "/workflows", {
            params: { compID: props.comp_id },
        }).then((response) => {
            setCompanyWorkflows(response.data.filter((wf) => wf.wf_status === 1));
        }).catch((error) => {
            console.log(error);
        });
    };

    const fetchCustomerData = async () => {
        await axios.get(APIURL + "/customers/v2/associated", {
            params: { company_id: props.user.comp_id, employee_id: props.user.emp_id, type: props.user.permissions.AdminLevelCustomers ? "Admin": "Manager", program_id: props.selectedProgramId ?? undefined },
        }).then((response) => {
            setCustomerList(response.data.filter((cus) => cus.cus_status === 'Active'));
        }).catch((error) => {
            console.log(error);
        });
    };

    const fetchEmployeeData = async () => {
        if (props.user.type === 'Admin') {
            await axios.get(APIURL + '/dropdowns/dropdownType/employeeslist', {
                params: { comp_id: props.user.comp_id, program_id: props.selectedProgramId ?? undefined }
            }).then((response) => {
                const empList = response.data.map((emp) => { return { emp_id: emp.id, emp_name: emp.employee_name } });
                setEmployeeList(empList);
            }).catch((error) => {
                console.log(error);
            });
        } else if (props.user.type.includes('Manager')) {
            await axios.get(APIURL + "/employees/v2/associated", {
                params: { company_id: props.user.comp_id, employee_id: props.user.emp_id, program_id: props.selectedProgramId ?? undefined },
            }).then((response) => {
                let associatedEmployees = response.data.directlyManaged.concat(response.data.hr_managed).concat({ emp_id: props.user.emp_id, emp_name: props.user.emp_name });

                associatedEmployees = associatedEmployees.filter((obj, index, self) => {
                    return index === self.findIndex((t) => (
                        t.emp_id === obj.emp_id
                    ));
                }).map((emp) => { return { emp_id: emp.emp_id, emp_name: emp.emp_name ? emp.emp_name : emp.emp_first_name + ' ' + emp.emp_last_name } });

                setEmployeeList(associatedEmployees);
            }).catch((error) => {
                console.log(error);
            });
        } else {
            setEmployeeList([{ emp_id: props.user.emp_id, emp_name: props.user.emp_name }]);
        }
    };

    const handleSave = async () => {
        setSavingWfInstance(true);

        await axios
            .post(APIURL + `/workflows/instance`, {
                wf_instanceWF_ID: selectedWorkflow.wf_id,
                wf_instanceStatus: 0,
                wf_instanceCustomer: selectedCustomer.cus_id,
                wf_instanceEmployee: selectedEmployee.emp_id,
                wf_instanceStartDate: moment(wfStartDate).format('MM-DD-YYYY'),
                wf_instanceEndDate: moment(wfEndDate).format('MM-DD-YYYY'),
                wf_instanceCreatedBy: props.user.emp_id,
            })
            .then(function (response) {
                setAlertMessage('Workflow instantiated successfully!');
                setAlertType('success');
            })
            .catch(function (error) {
                console.log(error);
                setAlertMessage('An error occurred while instantiating the workflow!');
                setAlertType('error');
            });
    };

    const SnackbarComponent = () => {
        return (
            <div>
                <Snackbar open={alertMessage ? true : false} autoHideDuration={2000} onClose={handleSnackbarAlertClose}>
                    <Alert severity={alertType} elevation={6} variant="filled">
                        {alertMessage}
                        <IconButton
                            sx={{ p: 0.25 }}
                            onClick={handleSnackbarAlertClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Alert>
                </Snackbar>
            </div>
        );
    };

    const handleSnackbarAlertClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertMessage('');
        setAlertType('');
        props?.handleClose?.();
        setSavingWfInstance(false);
    }

    if (loading) {
        return (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </div>
        );
    } else {
        return (
            <div>
                <h4>New Workflow</h4>
                <div style={{ marginTop: 20 }}>
                    <Autocomplete
                        id="wf-select"
                        options={companyWorkflows}
                        value={selectedWorkflow}
                        getOptionLabel={(option) => option.wf_title}
                        isOptionEqualToValue={(option, value) => option.wf_id === value.wf_id}
                        onChange={(event, value) => { setSelectedWorkflow(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.wf_id}>
                                {option.wf_title}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Workflow" />}
                        sx={{ marginBottom: 3 }}
                    />
                    <Autocomplete
                        id="client-select"
                        options={customerList}
                        value={selectedCustomer}
                        getOptionLabel={(option) => option.cus_name}
                        isOptionEqualToValue={(option, value) => option.cus_id === value.cus_id}
                        onChange={(event, value) => { setSelectedCustomer(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.cus_id}>
                                {option.cus_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Customer" />}
                        sx={{ marginBottom: 3 }}
                    />
                    <Autocomplete
                        id="employee-select"
                        options={employeeList}
                        value={selectedEmployee}
                        getOptionLabel={(option) => option.emp_name}
                        isOptionEqualToValue={(option, value) => option.emp_id === value.emp_id}
                        onChange={(event, value) => { setSelectedEmployee(value); }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.emp_id}>
                                {option.emp_name}
                            </li>
                        )}
                        renderInput={(params) => <TextField {...params} label="Select Employee" />}
                        sx={{ marginBottom: 3 }}
                    />
                    <Grid>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Workflow Start Date"
                                value={wfStartDate}
                                onChange={(newValue) => { setWfStartDate(newValue); }}
                                disablePast
                                sx={{ width: '49%', marginRight: 1, marginBottom: 3 }}
                            />
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Workflow End Date"
                                value={wfEndDate}
                                onChange={(newValue) => { setWfEndDate(newValue); }}
                                disablePast
                                sx={{ width: '49%', marginBottom: 3 }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleSave}
                        disabled={savingWfInstance || !selectedWorkflow || !selectedCustomer || !selectedEmployee}
                        sx={{ width: '100%' }}
                    >
                        Instantiate Workflow
                    </Button>
                </div>
                {SnackbarComponent()}
            </div>
        );
    }
};